import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import * as Sentry from '@sentry/react';
import { persistor, store } from './store';
import App from './App';
import * as serviceWorker from './serviceWorker';
import LoadingView from './views/loading/LoadingView';
import { CHUNK_ERROR_PATTERNS, installChunkErrorRecovery } from './services/helpers/LazyWithRetry';
import { FORM_VALIDATION_ERROR_PATTERNS } from './services/helpers/SentryFilters';
import { installDomMutationGuard } from './services/helpers/DomMutationGuard';
import Analytics from './services/analytics/Analytics';
import { isGoogleBusinessOAuthCallback } from './services/google-business/GoogleBusinessOAuth';
import GoogleBusinessOAuthCallbackView from './views/oauth/google-business-callback';
import './styles/index.css';
import 'react-toastify/dist/ReactToastify.css';
import './services/localization/Localization';
import './services/exports/Logger';
import 'react-circular-progressbar/dist/styles.css';
import 'react-fontpicker-ts/dist/index.css';
import './styles/global.css';

/**
 * Anchored origin patterns for the Foodamigos APIs, so `sentry-trace`/`baggage` are only
 * attached to our own backends and never to third-party hosts.
 */
function apiOriginPatterns(baseUrls) {
  return baseUrls.flatMap((baseUrl) => {
    try {
      const { origin } = new URL(baseUrl);

      return [new RegExp(`^${origin.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(/|$)`)];
    } catch {
      return [];
    }
  });
}

/**
 * Parses the trace sample rate from env; anything that is not a number in [0, 1] (empty,
 * a typo, a percentage) falls back to the default instead of silently disabling tracing.
 */
function traceSampleRate(value, fallback = 0.05) {
  const rate = parseFloat(value);

  return Number.isFinite(rate) && rate >= 0 && rate <= 1 ? rate : fallback;
}

if (!import.meta.env.DEV) {
  installChunkErrorRecovery();
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    normalizeDepth: 10,
    // Lower-case: the Sentry alert rules filter on `environment:production`.
    environment: import.meta.env.VITE_APP_MODE?.toLowerCase(),
    ignoreErrors: [...CHUNK_ERROR_PATTERNS, ...FORM_VALIDATION_ERROR_PATTERNS],
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: traceSampleRate(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE),
    tracePropagationTargets: apiOriginPatterns([
      import.meta.env.VITE_API_BASE_URL,
      import.meta.env.VITE_SSO_API_BASE_URL,
      import.meta.env.VITE_RESERVATION_API_BASE_URL,
      import.meta.env.VITE_STOREFRONT_API_BASE_URL,
      import.meta.env.VITE_EMAIL_MARKETING_API_BASE_URL,
      import.meta.env.VITE_POS_BRIDGE_API_BASE_URL,
    ]),
  });
}

Analytics.init(import.meta.env.VITE_MIXPANEL_TOKEN);

installDomMutationGuard();

const root = createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    {/* Google's sign-in callback runs on the neutral host, usually without a session
        there: it must not boot the dashboard (auth, brand, tenant data) around it. */}
    {isGoogleBusinessOAuthCallback(window.location.pathname) ? (
      <GoogleBusinessOAuthCallbackView />
    ) : (
      <Provider store={store}>
        <PersistGate loading={<LoadingView />} persistor={persistor}>
          <App />
        </PersistGate>
      </Provider>
    )}
  </React.StrictMode>,
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: http://bit.ly/CRA-PWA
serviceWorker.unregister();
